import React, { useEffect, useState } from 'react';
import { X, Users, Monitor, User, Calendar } from 'lucide-react';
import { getAttendeesForMeeting } from '../data/meetingAttendeesDatabase';
import { getAllUsers } from '../data/userDatabase.ts';
import { format } from 'date-fns';

interface AttendeesModalProps {
  meetingId: string;
  onClose: () => void;
}

interface MeetingAttendee {
  id: string;
  meetingId: string;
  userId: string;
  userName: string;
  attendanceType: 'in-person' | 'virtual' | 'not-attending';
  attendedAt: any;
}

export default function AttendeesModal({ meetingId, onClose }: AttendeesModalProps) {
  const [attendees, setAttendees] = useState<MeetingAttendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unconfirmedCount, setUnconfirmedCount] = useState(0);
  const [notAttendingCount, setNotAttendingCount] = useState(0);

  useEffect(() => {
    const loadAttendees = async () => {
      try {
        const [meetingAttendees, allUsers] = await Promise.all([
          getAttendeesForMeeting(meetingId),
          getAllUsers()
        ]);
        // Sort attendees by attendance type and name
        const sortedAttendees = meetingAttendees.sort((a, b) => {
          // First sort by attendance type (in-person first, then virtual)
          if (a.attendanceType === 'in-person' && b.attendanceType !== 'in-person') return -1;
          if (b.attendanceType === 'in-person' && a.attendanceType !== 'in-person') return 1;
          if (a.attendanceType === 'virtual' && b.attendanceType !== 'virtual') return -1;
          if (b.attendanceType === 'virtual' && a.attendanceType !== 'virtual') return 1;
          // Then sort by name (handle undefined/null values)
          const nameA = a.userName || 'Unknown User';
          const nameB = b.userName || 'Unknown User';
          return nameA.localeCompare(nameB);
        });
        setAttendees(sortedAttendees);
        // Count not attending
        setNotAttendingCount(meetingAttendees.filter(a => a.attendanceType === 'not-attending').length);
        // Count unconfirmed (users with no attendance record)
        const confirmedUserIds = meetingAttendees.map(a => a.userId);
        setUnconfirmedCount(allUsers.filter(u => !confirmedUserIds.includes(u.id)).length);
      } catch (error) {
        console.error('Failed to load attendees:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAttendees();
  }, [meetingId]);

  const getAttendanceTypeIcon = (type: 'in-person' | 'virtual' | 'not-attending') => {
    switch (type) {
      case 'in-person':
        return <User className="w-4 h-4 text-green-600" />;
      case 'virtual':
        return <Monitor className="w-4 h-4 text-blue-600" />;
      default:
        return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  const getAttendanceTypeLabel = (type: 'in-person' | 'virtual' | 'not-attending') => {
    switch (type) {
      case 'in-person':
        return 'In-person';
      case 'virtual':
        return 'Virtual';
      default:
        return 'Unknown';
    }
  };

  const getAttendanceTypeStyle = (type: 'in-person' | 'virtual' | 'not-attending') => {
    switch (type) {
      case 'in-person':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'virtual':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatAttendanceTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return format(date, 'MMM d, yyyy h:mm a');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-600" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Meeting Attendees</h3>
              <p className="text-sm text-gray-500">{attendees.length} {attendees.length === 1 ? 'person' : 'people'} attending</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : attendees.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No attendees yet</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {attendees
                .filter(attendee => attendee.attendanceType === 'in-person' || attendee.attendanceType === 'virtual')
                .map(attendee => (
                <li 
                  key={attendee.userId}
                  className="flex flex-col p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {getAttendanceTypeIcon(attendee.attendanceType)}
                      <span className="text-sm font-medium text-gray-900">{attendee.userName || 'Unknown User'}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full border ${getAttendanceTypeStyle(attendee.attendanceType)}`}>
                      {getAttendanceTypeLabel(attendee.attendanceType)}
                    </span>
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="w-3 h-3 mr-1" />
                    Confirmed {formatAttendanceTime(attendee.attendedAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex flex-col items-center mb-2 text-xs text-gray-500">
            <span>{unconfirmedCount} Unconfirmed</span>
            <span>{notAttendingCount} Not attending</span>
          </div>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
} 